import axios from 'axios';
import { getAuthToken } from '@redpanda/utils';
import { Role } from '@redpanda/types';

const API_URL = process.env.REACT_APP_ACCOUNTING_BACKEND_URL || 'http://localhost:5002/api';
const IDENTITY_API_URL = process.env.REACT_APP_IDENTITY_SERVICE_URL || 'http://localhost:3000/api';
const SUBSCRIPTION_API_URL = process.env.REACT_APP_SUBSCRIPTION_SERVICE_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// 添加請求攔截器自動設定 Authorization header
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const identityApi = axios.create({
  baseURL: IDENTITY_API_URL,
  withCredentials: true,
});

// 添加 identityApi 的請求攔截器
identityApi.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const subscriptionApi = axios.create({
  baseURL: SUBSCRIPTION_API_URL,
  withCredentials: true,
});

// 添加 subscriptionApi 的請求攔截器
subscriptionApi.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 網路錯誤檢測
const networkErrorHandler = (error: any) => {
  const isRealNetworkError = (
    (!error.response && (
      error.code === 'ERR_NETWORK' || 
      error.code === 'ECONNREFUSED' || 
      error.code === 'ERR_CONNECTION_REFUSED' ||
      error.code === 'ENOTFOUND' ||
      error.code === 'ENETUNREACH' ||
      error.code === 'EAI_AGAIN'
    )) ||
    (error.response?.status >= 500 && error.response?.status <= 599)
  );
  
  if (isRealNetworkError) {
    console.error('Network Error detected:', error);
    // window.location.href = '/maintenance.html'; // 暫時停用跳轉
    return;
  }
  
  return Promise.reject(error);
};

// 為所有 API 實例添加網路錯誤檢測
[api, identityApi, subscriptionApi].forEach(apiInstance => {
  apiInstance.interceptors.response.use(
    response => response,
    networkErrorHandler
  );
});

// --- 會計科目 APIs ---
export const getAccounts = (page = 1, pageSize = 20, filters: { search?: string; type?: string } = {}) =>
  api.get('/accounts', { params: { page, pageSize, ...filters } });
export const createAccount = (data: any) => api.post('/accounts', data);
export const updateAccount = (id: string, data: any) => api.patch(`/accounts/${id}`, data);
export const deleteAccount = (id: string) => api.delete(`/accounts/${id}`);
export const bulkDeleteAccounts = (ids: string[]) => api.delete('/accounts/bulk', { data: { ids } });
export const createDefaultAccounts = () => api.post('/accounts/defaults');

// --- 記帳分錄 APIs ---
export const getJournalEntries = (page = 1, pageSize = 20, filters: { search?: string; status?: 'POSTED' | 'DRAFT' } = {}) =>
  api.get('/journals', { params: { page, pageSize, ...filters } });
export const createJournalEntry = (data: any) => api.post('/journals', data);
export const updateJournalEntry = (id: string, data: any) => api.patch(`/journals/${id}`, data);
export const deleteJournalEntry = (id: string) => api.delete(`/journals/${id}`);
export const bulkDeleteJournalEntries = (ids: string[]) => api.delete('/journals/bulk', { data: { ids } });
/** 過帳／取消過帳：單筆 PATCH /:id/status、批次 PATCH /bulk/status */
export const setJournalPosted = (id: string, isPosted: boolean) => api.patch(`/journals/${id}/status`, { isPosted });
export const setJournalsPosted = (ids: string[], isPosted: boolean) => api.patch('/journals/bulk/status', { ids, isPosted });

// --- 發票 APIs ---
/** 發票列表：搜尋文字、狀態、年月由後端查全部發票（年份單獨＝整年） */
export interface InvoiceFilters { archived?: boolean; search?: string; status?: string; year?: string; month?: string }
export const getInvoices = (page = 1, pageSize = 20, filters: InvoiceFilters = {}) =>
  api.get('/invoices', { params: { page, pageSize, ...filters } });
export const createInvoice = (data: any) => api.post('/invoices', data);
export const updateInvoice = (id: string, data: any) => api.patch(`/invoices/${id}`, data);
/** 登記收款／刪除收款（後端同時處理銀行交易與分錄），回傳更新後的發票 */
export const recordInvoicePayment = (id: string, data: { date: string; amount: number; bankAccountId: string }) => api.post(`/invoices/${id}/payments`, data);
export const deleteInvoicePayment = (id: string, paymentId: string) => api.delete(`/invoices/${id}/payments/${paymentId}`);
/** 開立折讓／刪除折讓（後端同時處理分錄與退款交易），回傳更新後的發票 */
export const createCreditNote = (id: string, data: { date: string; subtotal: number; reason?: string | null; refundBankAccountId?: string | null }) =>
  api.post(`/invoices/${id}/credit-notes`, data);
export const deleteCreditNote = (id: string, creditNoteId: string) => api.delete(`/invoices/${id}/credit-notes/${creditNoteId}`);
export const updateInvoiceStatus = (id: string, status: string) => api.patch(`/invoices/${id}/status`, { status });
export const deleteInvoice = (id: string) => api.delete(`/invoices/${id}`);
export const bulkDeleteInvoices = (ids: string[]) => api.delete('/invoices/bulk', { data: { ids } });
/** 封存／恢復：PATCH /invoices/bulk { ids, archived } */
export const setInvoicesArchived = (ids: string[], archived: boolean) => api.patch('/invoices/bulk', { ids, archived });

// --- 儀表板、財務報表 APIs（?year=YYYY）---
export const getDashboard = (year: number) => api.get('/reports/dashboard', { params: { year } });
export const getTrialBalance = (year: number) => api.get('/reports/trial-balance', { params: { year } });
export const getIncomeStatement = (year: number) => api.get('/reports/income-statement', { params: { year } });
export const getBalanceSheet = (year: number) => api.get('/reports/balance-sheet', { params: { year } });

// --- 發票限制 APIs ---
export const getInvoiceLimitInfo = () => {
  return subscriptionApi.get(`/subscriptions/invoice-limit`);
};



// --- 用戶管理 APIs (Admin) ---
export const getApprovedUsers = () => identityApi.get('/admin/users/approved');
export const getPendingUsers = () => identityApi.get('/admin/users/pending');

// 用戶角色管理 (RESTful: PATCH /users/:id/role)
export const updateUserRole = (userId: string, role: Role) =>
  identityApi.patch(`/admin/users/${userId}/role`, { role });

// 核准／拒絕用戶：與 CRM 相同走 /approve、/reject（/approve 會設定 allowedSystems，預設 ALL）
export const approveUser = (userId: string, role: Role, config?: { allowedSystems?: string[] }) =>
  identityApi.post(`/admin/users/${userId}/approve`, { role, ...config });
export const rejectUser = (userId: string) => identityApi.post(`/admin/users/${userId}/reject`);

// 刪除用戶 (RESTful: DELETE /users/:id)
export const deleteUser = (userId: string) => identityApi.delete(`/admin/users/${userId}`);

// --- 會計年度、期間、結帳 APIs ---
export const getFiscalYears = (page = 1, pageSize = 50) => api.get('/fiscal-years', { params: { page, pageSize } });
export const createFiscalYear = (data: any) => api.post('/fiscal-years', data);
export const updateFiscalYear = (id: string, data: any) => api.patch(`/fiscal-years/${id}`, data);
export const deleteFiscalYear = (id: string) => api.delete(`/fiscal-years/${id}`);
export const getPeriodClosings = (page = 1, pageSize = 20) => api.get('/period-closings', { params: { page, pageSize } });
export const getPeriodClosingById = (id: string) => api.get(`/period-closings/${id}`);
export const startPeriodClosing = (data: { periodId: string; type: string; notes?: string }) => api.post('/period-closings', data);
export const updatePeriodClosingNotes = (id: string, notes: string) => api.patch(`/period-closings/${id}`, { notes });
/** 重新開啟已結帳的期間 */
export const reopenPeriod = (periodId: string) => api.patch(`/periods/${periodId}/status`, { isClosed: false });

// --- 期間比較 API（與前 N 期比較） ---
export interface ComparisonQuery { type: 'income' | 'balance'; unit: 'month' | 'quarter' | 'year'; end: string; count: number }
export const getComparison = (query: ComparisonQuery) => api.get('/reports/comparison', { params: query });

// === 稅務報表 APIs ===

/** 營業稅試算：period＝該期第一個月 YYYY-MM（兩個月一期） */
export const getTaxReport = (period: string) => api.get('/reports/tax', { params: { period } });

// === 銀行帳戶管理 APIs ===

export const bankAccountService = {
  getBankAccounts: (page = 1, pageSize = 20, search?: string) => api.get('/bank-accounts', { params: { page, pageSize, search } }),
  getBankAccountsSummary: () => api.get('/bank-accounts/summary'),
  getBankAccountById: (id: string) => api.get(`/bank-accounts/${id}`),
  createBankAccount: (data: any) => api.post('/bank-accounts', data),
  updateBankAccount: (id: string, data: any) => api.patch(`/bank-accounts/${id}`, data),
  deleteBankAccount: (id: string) => api.delete(`/bank-accounts/${id}`),
  getBankTransactions: (accountId: string, page = 1, pageSize = 20, filters: { search?: string; reconciled?: string } = {}) =>
    api.get(`/bank-accounts/${accountId}/transactions`, { params: { page, pageSize, ...filters } }),
  createBankTransaction: (accountId: string, data: any) => api.post(`/bank-accounts/${accountId}/transactions`, data),
  updateBankTransaction: (accountId: string, transactionId: string, data: any) =>
    api.patch(`/bank-accounts/${accountId}/transactions/${transactionId}`, data),
  deleteBankTransaction: (accountId: string, transactionId: string) => api.delete(`/bank-accounts/${accountId}/transactions/${transactionId}`),
  bulkDeleteBankTransactions: (accountId: string, ids: string[]) => api.delete(`/bank-accounts/${accountId}/transactions/bulk`, { data: { ids } }),
};

// === 銀行對帳管理 APIs ===

export const bankReconciliationService = {
  /** 匯入對帳單（CSV／Excel） */
  importStatement: (bankAccountId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('bankAccountId', bankAccountId);
    return api.post('/bank-reconciliation/imports', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getImportRecords: (bankAccountId: string, page = 1, pageSize = 20) =>
    api.get('/bank-reconciliation/imports', { params: { bankAccountId, page, pageSize } }),
  deleteImportRecord: (importId: string) => api.delete(`/bank-reconciliation/imports/${importId}`),
  getStatistics: (bankAccountId: string) => api.get('/bank-reconciliation/statistics', { params: { bankAccountId } }),
  /** 自動比對（信心度 80 以上直接套用） */
  autoMatch: (bankAccountId: string) => api.post('/bank-reconciliation/matches', { bankAccountId }),
  /** 取消對帳（銀行交易與配對的對帳單明細一起改回未對帳） */
  unmatch: (bankTransactionId: string) => api.delete(`/bank-reconciliation/matches/${bankTransactionId}`),
};





// === 資料匯入/匯出 APIs ===

// 匯出為 Excel
export const exportToExcel = (sheets: string[], language = 'zh') => {
  return api.post('/data-io/export/excel', { sheets, language }, {
    responseType: 'blob',
  });
};

// 從 Excel 匯入
export const importFromExcel = (file: File, language = 'zh', onUploadProgress?: (progressEvent: any) => void) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('language', language);
  return api.post('/data-io/import/excel', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress,
  });
};

// CRM 客戶（發票「客戶」選單；由會計後端用服務間密鑰向 CRM 查，沒有 CRM 時回空陣列）
export interface CrmCustomer { id: string; name: string; company: string | null; address: string | null }
export const getCrmCustomers = () => api.get<CrmCustomer[]>('/crm/customers');

export default api;